import { useState, useEffect, useCallback, useMemo } from "react";
import StepCard from "./StepCard";
import QuestionForm from "./QuestionForm";
import FeedbackModal from "../Tax Filling/FeedbackModal";
import ReferralRewardModal from "../../components/ReferralRewardModal";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../context/SocketContext";
import { useRealtimeRefresh } from "../../context/RealtimeSyncContext";
import { PageHeaderSkeleton, StepCardsSkeleton } from "../../components/ui/Skeleton";
import { toast } from "../../components/ui/Toast";
import {
  usePortalContent,
  getUsaFormationPageMeta,
  mergeUsaFormationSteps,
  getActiveFormSections,
  countActiveQuestions,
  getUsaStepCardMeta,
  formatUsaTemplate,
} from "../../portal";

const USACompanyFormation = () => {
  const { currentThemeColors } = useTheme();
  const { content: usaContent, loading: portalLoading } = usePortalContent('usa-company-formation');
  const usaMeta = useMemo(() => getUsaFormationPageMeta(usaContent), [usaContent]);
  const stepsConfig = usaMeta.stepCards;

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
  };
  const [steps, setSteps] = useState([]);

  const [isFormVisible, setIsFormVisible] = useState(false);
  const [assignedCompanyForm, setAssignedCompanyForm] = useState("LLC");
  const [isInitialized, setIsInitialized] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);

  // Calculate total questions based on company type
  const getTotalQuestions = () => {
    const sections = getActiveFormSections(usaMeta.forms, assignedCompanyForm);
    return countActiveQuestions(sections) || (assignedCompanyForm === "C-Corp" ? 30 : 26);
  };

  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const fetchUserData = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/user`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to fetch user data");

      const data = await response.json();
      setUserData(data.user);

      const companyForm = data.user.assignedCompanyForm || "LLC";
      setAssignedCompanyForm(companyForm);

      if (data.user.companySteps && data.user.companySteps.length > 0) {
        setSteps(mergeUsaFormationSteps(data.user.companySteps, stepsConfig));

        const step1 = data.user.companySteps.find(s => s.step === 1);
        if (step1 && step1.answers && step1.answers.businessType) {
          setAssignedCompanyForm(step1.answers.businessType);
        }
      } else {
        setSteps(mergeUsaFormationSteps([], stepsConfig));
      }

      setIsInitialized(true);
    } catch (error) {
      console.error(error);
      setError('Failed to load user data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [navigate, stepsConfig]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  useRealtimeRefresh(['usa-formation', 'profile'], fetchUserData);

  useEffect(() => {
    if (!isInitialized || portalLoading) return;
    setSteps((prev) => mergeUsaFormationSteps(prev, stepsConfig));
  }, [stepsConfig, portalLoading, isInitialized]);

  useEffect(() => {
    if (userData && isInitialized) {
      const step3Done = steps.some(s => s.step === 3 && s.status === 'submitted');
      if (step3Done && (!userData.usaFeedback || !userData.usaFeedback.rating)) {
        setShowFeedbackModal(true);
      }
    }
  }, [steps, userData, isInitialized]);

  const handleFeedbackSubmit = async (rating, comment) => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.warning(usaMeta.toasts.feedbackLoginRequired || "Please log in to submit feedback.");
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/submit-feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          userId: userData._id,
          rating,
          comment: comment || '',
          serviceType: 'usa'
        }),
      });

      const responseData = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(responseData.message || `Failed to submit feedback: ${response.status}`);
      }

      setUserData(prev => ({
        ...prev,
        usaFeedback: {
          rating,
          comment: comment || '',
          submittedAt: new Date()
        }
      }));
      setShowFeedbackModal(false);
      setShowReferralModal(true);
      // alert("Thank you for your feedback!"); // Removing alert to keep it smooth to the next modal
    } catch (error) {
      console.error('Feedback submission error:', error);
      toast.error(formatUsaTemplate(usaMeta.toasts.feedbackFailed || 'Failed to submit feedback: {message}', { message: error.message || 'Please try again.' }));
    }
  };

  const handleStepClick = (stepNumber) => {
    if (stepNumber === 1) {
      if (steps[0].status === "submitted") {
        toast.warning(usaMeta.toasts.stepAlreadySubmitted || "Step 1 has already been submitted and cannot be reopened.");
        return;
      }
      setIsFormVisible(true);
    } else if (stepNumber === 2) {
      navigate("/documents", { state: { openFolder: "Post Formation documents" } });
    } else if (stepNumber === 3) {
      navigate("/documents", { state: { openFolder: "Tax ID (EIN)" } });
    }
  };

  const handleFormSubmit = async (data, isFinalSubmit) => {
    const token = localStorage.getItem("token");

    // Check if business type has changed
    const newBusinessType = data.answers && data.answers.businessType;
    if (newBusinessType && newBusinessType !== assignedCompanyForm) {
      setAssignedCompanyForm(newBusinessType);
    }

    // Determine the new status based on whether this is a final submit
    const newStatus = isFinalSubmit ? "submitted" : "in-progress";

    // Use the progress from data if available
    const progress = (data.progress !== undefined) ? data.progress : steps[0].progress;

    // First, update the UI optimistically
    const updatedSteps = steps.map((s) => {
      if (s.step === 1) {
        return {
          ...s,
          status: newStatus,
          progress: progress,
          answers: data.answers,
          comments: data.comments,
          file: data.file || s.file,
          error: false,
          errorMessage: ''
        };
      }
      // If this is a final submit, update step 2 to in-progress
      if (s.step === 2 && isFinalSubmit) {
        return {
          ...s,
          status: "in-progress"
        };
      }
      return s;
    });

    // Update the UI immediately
    setSteps(updatedSteps);
    setError(null);

    try {
      // Prepare the data in the format expected by the backend
      const requestData = {
        steps: updatedSteps.map(step => ({
          step: step.step,
          title: step.title,
          status: step.status,
          progress: step.progress || 0,
          answers: step.answers || {},
          comments: step.comments || {},
          file: step.file || null
        })),
        answers: data.answers || {},
        comments: data.comments || {},
        file: data.file || null
      };

      console.log('Sending update request:', requestData);

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/usa-company-formation/update-steps`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestData),
      });

      const responseData = await response.json().catch(() => ({}));
      console.log('Update response:', { status: response.status, responseData });

      if (!response.ok) {
        throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
      }

      // If we get here, the update was successful
      if (isFinalSubmit) {
        toast.success(usaMeta.toasts.formSubmitted || "Form submitted successfully!");
        setIsFormVisible(false);
      }
    } catch (error) {
      console.error('Form submission error:', error);

      // Update steps with error state
      const errorSteps = steps.map((s) => {
        if (s.step === 1) {
          return {
            ...s,
            status: 'in-progress',
            error: true,
            errorMessage: error.message || 'Failed to save progress. Please try again.'
          };
        }
        return s;
      });

      setSteps(errorSteps);
      setError(error.message || 'Failed to save progress. Please try again.');
      toast.error(formatUsaTemplate(usaMeta.toasts.saveFailed || error.message || 'Failed to save progress. Please try again.', { message: error.message }));
    }
  };

  // Don't render anything until we've finished initializing
  if (loading || portalLoading || !isInitialized) {
    return (
      <div className="space-y-6">
        <PageHeaderSkeleton />
        <StepCardsSkeleton count={4} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 mb-4">
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 rounded-xl bg-primary-500 text-white hover:bg-primary-600 transition-colors"
          >
            {usaMeta.errorRetryLabel || 'Retry'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Instruction Banner */}
      <div
        className="relative overflow-hidden rounded-2xl px-4 py-2 shadow-lg opacity-0 animate-fade-in-up"
        style={{
          background: `linear-gradient(135deg, var(--primary-600), var(--primary-700))`,
          animationDelay: '0ms',
          animationFillMode: 'forwards',
        }}
      >
        <div className="relative flex items-center gap-3 text-white">
          <span className="text-2xl shrink-0">🏢</span>
          <marquee
            className="text-sm md:text-base font-medium"
            behavior="scroll"
            direction="left"
            scrollamount="5"
          >
            {usaMeta.instructionBanner || 'USA Formation: Start with Company Formation Checklist ➡️ Post Formation Documents ➡️ Tax ID (EIN) | 💬 Need help? Use the chat feature to communicate with our experts!'}
          </marquee>
        </div>
      </div>

      {/* Page Header */}
      <div className="card p-5 sm:p-7 opacity-0 animate-fade-in-up" style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
          <div>
            <h1 className="page-title">{userData ? userData.name : ''}</h1>
            {userData?.email && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{userData.email}</p>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 text-sm text-gray-600 dark:text-gray-400">
            <span className="px-3 py-1 rounded-full bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 font-semibold">
              {usaMeta.pageBadgeLabel || 'USA Company Formation'}
            </span>
            <span className="px-3 py-1 rounded-full bg-secondary-50 dark:bg-secondary-900/30 text-secondary-700 dark:text-secondary-400 font-semibold">
              {assignedCompanyForm}
            </span>
          </div>
        </div>
      </div>

      {/* Steps Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6" style={{ minHeight: '200px' }}>
        {steps.map((s, index) => {
          const cardMeta = getUsaStepCardMeta(stepsConfig, s.step);
          return (
          <div
            key={`wrapper-${s.step}`}
            className="opacity-0 animate-fade-in-up h-full"
            style={{ animationDelay: `${200 + index * 100}ms`, animationFillMode: 'forwards' }}
          >
            <StepCard
              step={s.step}
              title={s.title}
              status={s.status}
              progress={s.progress}
              answers={s.answers}
              totalQuestions={s.step === 1 ? getTotalQuestions() : 1}
              error={s.error || false}
              errorMessage={s.errorMessage || ''}
              submittedAt={s.submittedAt}
              customInstructions={cardMeta.instructions}
              customStatusMessage={cardMeta.statusMessage}
              stepCardLabels={usaMeta.stepCardLabels}
              onClick={() => handleStepClick(s.step)}
            />
          </div>
        );})}
      </div>

      {isFormVisible && (
        <QuestionForm
          onClose={() => setIsFormVisible(false)}
          onSubmit={(data, isFinal) => handleFormSubmit(data, isFinal)}
          initialAnswers={steps[0].answers}
          initialComments={steps[0].comments}
          isSubmitted={steps[0].status === "submitted"}
          companyType={assignedCompanyForm}
          forms={usaMeta.forms}
          checklistModal={usaMeta.checklistModal}
          toasts={usaMeta.toasts}
          pdfMeta={usaMeta.pdf}
        />
      )}

      {showFeedbackModal && (
        <FeedbackModal
          onClose={() => setShowFeedbackModal(false)}
          onSubmit={handleFeedbackSubmit}
        />
      )}

      {showReferralModal && (
        <ReferralRewardModal
          isOpen={showReferralModal}
          onClose={() => setShowReferralModal(false)}
        />
      )}
    </div>
  );
};

export default USACompanyFormation;